import React from 'react';

import { Download as DownloadIcon } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';

import { useScreenInfo } from 'src/hooks/common';
import type { DroppedFile } from 'src/types/file';
import { convertFileSize } from 'src/utils/file';

type FileListItemStatusProps = {
  fileInfo: DroppedFile;
  validationEnabled?: boolean;
  uploadingEnabled?: boolean;
};

export const FileListItemStatus: React.FC<FileListItemStatusProps> = ({
  fileInfo,
  validationEnabled,
  uploadingEnabled,
}) => {
  const { isMobile } = useScreenInfo();
  const renderValidationStatuses = () => {
    if (!validationEnabled || !fileInfo.valid) return null;

    return (
      <React.Fragment>
        <Typography variant="body2" component="span">
          {' • '}
        </Typography>
        {!fileInfo.valid && fileInfo.validationInProgress && !fileInfo.validationComplete && (
          <Typography variant="body2" component="span" color="info">
            Validating...
          </Typography>
        )}
        {fileInfo.valid && fileInfo.validationComplete && (
          <Typography variant="body2" component="span" color="green">
            Valid
          </Typography>
        )}
      </React.Fragment>
    );
  };

  const renderUploadingStatuses = () => {
    if (validationEnabled && !fileInfo.validationComplete) return null;
    if (validationEnabled && !fileInfo.valid) return null;
    if (!uploadingEnabled) return null;

    return (
      <React.Fragment>
        <Typography variant="body2" component="span">
          {' • '}
        </Typography>
        {fileInfo.loading && !fileInfo.loadingComplete && (
          <Typography variant="body2" component="span" color="info">
            Uploading...
          </Typography>
        )}
        {!fileInfo.loading && fileInfo.loadingComplete && (
          <Typography variant="body2" component="span" color="green">
            Uploaded
          </Typography>
        )}
        {isMobile && fileInfo.uploadedFileUrl && (
          <React.Fragment>
            <Typography variant="body2" component="span">
              {' • '}
            </Typography>
            <IconButton
              sx={{ p: 0 }}
              href={fileInfo.uploadedFileUrl}
              target="_blank"
              disabled={fileInfo.loading}
            >
              <DownloadIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  const renderErrorMessage = () => {
    if (!fileInfo.errorMessage) return null;

    return (
      <React.Fragment>
        <Typography variant="body2" component="span">
          {' • '}
        </Typography>
        <Typography variant="body2" component="span" color="error">
          {fileInfo.errorMessage}
        </Typography>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <Typography variant="body2" component="span">
        {convertFileSize(fileInfo.file?.size || fileInfo.size)}
      </Typography>

      {renderValidationStatuses()}
      {renderUploadingStatuses()}
      {renderErrorMessage()}
    </React.Fragment>
  );
};
